// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// Import the OrderForm component
import OrderForm from './components/OrderForm';
// TrackShipment component is removed as it's now a separate application
// import TrackShipment from './components/TrackShipment';

// Initialize Firebase (client-side)
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

function App() {
    // activeSection is now implicitly 'order' as it's the only section
    const [activeSection, setActiveSection] = useState('order'); // Default to 'order'
    const [userId, setUserId] = useState(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [stripeClient, setStripeClient] = useState(null); // New state to hold the resolved Stripe instance
    const [stripeLoadError, setStripeLoadError] = useState(false); // New state for Stripe load error

    // Determine backend URL based on environment
    const backendUrl = process.env.NODE_ENV === 'production'
        ? '/api' // Vercel's default API route prefix for serverless functions
        : 'http://localhost:5000'; // Local backend URL for development (no /api prefix here)

    useEffect(() => {
        // Sign in anonymously if no user is logged in
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUserId(user.uid);
            } else {
                signInAnonymously(auth)
                    .then((userCredential) => {
                        setUserId(userCredential.user.uid);
                        console.log("Signed in anonymously. User ID:", userCredential.user.uid);
                    })
                    .catch((error) => {
                        console.error("Error signing in anonymously:", error);
                    });
            }
            setLoadingAuth(false);
        });

        // Load Stripe.js and set the resolved Stripe instance
        const loadStripeInstance = async () => {
            try {
                const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
                if (stripe) {
                    setStripeClient(stripe);
                } else {
                    setStripeLoadError(true);
                    console.error("Stripe.js failed to load: loadStripe returned null.");
                }
            } catch (error) {
                console.error("Failed to load Stripe.js:", error);
                setStripeLoadError(true);
            }
        };

        loadStripeInstance();

        return () => {
            unsubscribeAuth(); // Cleanup auth subscription
        };
    }, []);

    if (loadingAuth) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                <p className="text-2xl font-semibold">Loading Blue Lane Express...</p>
            </div>
        );
    }

    // Display an error if Stripe failed to load
    if (stripeLoadError) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-red-100 text-red-700">
                <p className="text-2xl font-semibold">
                    Error loading payment system. Please ensure `REACT_APP_STRIPE_PUBLISHABLE_KEY` is correctly set and try again.
                </p>
            </div>
        );
    }

    // Only render Elements once stripeClient is available
    if (!stripeClient) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-700">
                <p className="text-2xl font-semibold">Initializing payment system...</p>
            </div>
        );
    }

    return (
        <Elements stripe={stripeClient}>
            <div className="min-h-screen bg-gray-100 flex flex-col font-inter">
                {/* Header */}
                <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 shadow-lg">
                    <div className="container mx-auto flex justify-between items-center">
                        <h1 className="text-4xl font-extrabold tracking-tight">
                            <span className="block">Blue Lane Express</span>
                        </h1>
                        {userId && (
                            <div className="text-sm">
                                User ID: <span className="font-mono bg-blue-700 px-2 py-1 rounded-md text-blue-100">{userId}</span>
                            </div>
                        )}
                        <nav>
                            <ul className="flex space-x-6">
                                <li>
                                    <a
                                        href="https://bluelaneexpress.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-white hover:text-blue-200 transition duration-300 ease-in-out text-lg font-medium"
                                    >
                                        Home
                                    </a>
                                </li>
                                <li>
                                    <button
                                        onClick={() => setActiveSection('order')}
                                        className={`text-white hover:text-blue-200 transition duration-300 ease-in-out text-lg font-medium ${activeSection === 'order' ? 'border-b-2 border-blue-200 pb-1' : ''}`}
                                    >
                                        Place Order
                                    </button>
                                </li>
                                {/* Removed Track Shipment and About Us navigation buttons */}
                            </ul>
                        </nav>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-grow container mx-auto p-8">
                    {/* Only render OrderForm */}
                    {activeSection === 'order' && (
                        <OrderForm
                            userId={userId}
                            backendUrl={backendUrl}
                            stripeClient={stripeClient}
                        />
                    )}
                    {/* Removed conditional rendering for TrackShipment and About Us */}
                </main>

                {/* Footer */}
                <footer className="bg-gray-800 text-white p-6 text-center shadow-inner mt-8">
                    <p>&copy; {new Date().getFullYear()} Blue Lane Express. All rights reserved.</p>
                </footer>
            </div>
        </Elements>
    );
}

export default App;
